import { addDays, addMonths, addWeeks, addYears, format, lastDayOfMonth, parseISO, startOfDay } from 'date-fns';

import { FactorDateFrame, FrequencyType } from '@ndvr/portfolio-api';

const parseDate = (date: string) => parseISO(date.slice(0, 10));
const formatDate = (date: Date) => format(date, 'yyyy-MM-dd');

const incrementDate = (date: Date, frequency: FrequencyType): Date => {
    switch (frequency) {
        case FrequencyType.DAILY:
            return addDays(date, 1);
        case FrequencyType.WEEKLY:
            return addWeeks(date, 1);
        case FrequencyType.BI_WEEKLY:
            return addWeeks(date, 2);
        case FrequencyType.SEMI_MONTHLY: {
            const middleOfMonth = new Date(date.getFullYear(), date.getMonth(), 15);
            const monthEnd = lastDayOfMonth(date);
            if (date < middleOfMonth) return middleOfMonth;
            if (date < monthEnd) return monthEnd;
            return new Date(date.getFullYear(), date.getMonth() + 1, 15);
        }
        case FrequencyType.MONTHLY:
            return addMonths(date, 1);
        case FrequencyType.QUARTERLY:
            return addMonths(date, 3);
        case FrequencyType.SEMI_ANNUALLY:
            return addMonths(date, 6);
        case FrequencyType.ANNUALLY:
            return addYears(date, 1);
        case FrequencyType.ONE_TIME:
            return date;
    }
};

// End-exclusive, so adjacent projection periods never double-count an occurrence.
export const getFactorOccurrences = (frame: FactorDateFrame, from: Date, to: Date): string[] => {
    const endDate = frame.endDate ? parseDate(frame.endDate) : undefined;
    const dates: string[] = [];
    let occurrence = parseDate(frame.startDate);
    let count = 1;
    while (occurrence < to && (!endDate || occurrence <= endDate)) {
        if (frame.occurrenceCount !== undefined && count > frame.occurrenceCount) break;
        if (occurrence >= from) dates.push(formatDate(occurrence));
        if (frame.frequencyType === FrequencyType.ONE_TIME) break;
        occurrence = incrementDate(occurrence, frame.frequencyType);
        count += 1;
    }
    return dates;
};

export const getNextFactorOccurrence = (
    factorDateFrame: FactorDateFrame,
    today = startOfDay(new Date()),
): string | undefined => {
    const endDate = factorDateFrame.endDate ? parseDate(factorDateFrame.endDate) : undefined;
    let occurrence = parseDate(factorDateFrame.startDate);
    let occurrenceNumber = 1;

    while (!endDate || occurrence <= endDate) {
        if (occurrence >= today) return formatDate(occurrence);
        if (
            factorDateFrame.frequencyType === FrequencyType.ONE_TIME ||
            (factorDateFrame.occurrenceCount !== undefined && occurrenceNumber >= factorDateFrame.occurrenceCount)
        ) {
            return undefined;
        }

        occurrence = incrementDate(occurrence, factorDateFrame.frequencyType);
        occurrenceNumber += 1;
    }

    return undefined;
};
